import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { WireframeBackground } from '@/components/WireframeBackground';
import { Separator } from '@/components/ui/separator';
import { CHEATSHEET_ENTRIES } from '@/cheatsheet/cheatsheetData';
import { CheatsheetCard } from '@/cheatsheet/CheatsheetCard';
import {
  CheatsheetFilters,
  DEFAULT_FILTERS,
  type FilterState,
} from '@/cheatsheet/CheatsheetFilters';
import { ParserTester } from '@/cheatsheet/ParserTester';

export function Cheatsheet() {
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);

  const filtered = useMemo(() => {
    const q = filters.search.toLowerCase().trim();
    return CHEATSHEET_ENTRIES.filter((e) => {
      if (filters.level !== 'all' && e.level !== filters.level) return false;
      if (filters.dataType !== 'all' && e.dataType !== filters.dataType)
        return false;
      if (filters.uiSize !== 'all' && e.uiSize !== filters.uiSize) return false;
      if (filters.origin !== 'all' && e.origin !== filters.origin) return false;
      if (
        q &&
        !e.title.toLowerCase().includes(q) &&
        !e.description.toLowerCase().includes(q) &&
        !e.snippet.toLowerCase().includes(q)
      )
        return false;
      return true;
    });
  }, [filters]);

  return (
    <div className="relative min-h-full bg-background px-4 py-12">
      <WireframeBackground />
      <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6">
        {/* Page header */}
        <div className="mb-8">
          <Link
            to="/"
            className="mb-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to home
          </Link>
          <h1 className="text-3xl font-bold tracking-tight">
            Parameter Cheatsheet
          </h1>
          <p className="mt-2 text-muted-foreground">
            Every parameter type in OpenSCAD and Meshpit, with syntax, examples,
            and live input previews.
          </p>
        </div>

        <Separator className="mb-8" />

        {/* Live parser tester */}
        <div className="mb-8">
          <ParserTester />
        </div>

        {/* Filters */}
        <div className="mb-6">
          <CheatsheetFilters
            filters={filters}
            onChange={setFilters}
            resultCount={filtered.length}
            totalCount={CHEATSHEET_ENTRIES.length}
          />
        </div>

        {/* Card grid — two columns on md+, one on mobile */}
        {filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border py-16 text-center text-sm text-muted-foreground">
            No parameter types match the selected filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {filtered.map((entry) => (
              <CheatsheetCard key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
